/**
 * «¿Cómo nos conoció?» (`how_known_us`): las opciones y el aviso (23/9/2026).
 *
 * DECISIÓN DE DIRECCIÓN (23/9/2026): es OBLIGATORIO al dar de alta un paciente,
 * al editar su ficha y al cerrar la LOPD. El indicador de dirección
 * «Captación por origen» enseñaba que casi todas las consultas no tenían
 * origen. Para no bloquear a nadie cuando el paciente de verdad no lo sabe hay
 * una respuesta explícita, «No lo recuerda», que vale y es distinta de «Sin
 * origen» (no haber preguntado).
 *
 * La regla y el aviso los impone el servidor (`backend/services/
 * origenes_captacion.py`). En pantalla es un DESPLEGABLE, no texto libre: el
 * KPI agrupa por estos textos y «instagram» e «Insta» serían dos orígenes
 * distintos.
 *
 * LAS OPCIONES SON LAS DEL CATÁLOGO, Y SOLO DEL SERVIDOR (V2, 28/9/2026). El
 * catálogo lo edita dirección (pestaña «Captación por origen») y aquí no hay
 * copia: `useOpcionesComoNosConocio` pide los nombres vigentes
 * (`/api/origenes-captacion/nombres` con sesión, `/api/public/origenes-
 * captacion` en la LOPD de casa y del iPad, que además trae cómo se le enseña
 * cada nombre al paciente). Si la petición falla se vuelve a pedir; mientras
 * tanto se ofrece «No lo recuerda», la única respuesta que no depende del
 * catálogo, para no dejar vacío un desplegable obligatorio.
 */
import { useEffect, useState } from 'react';
import { api } from './api';

export const NO_LO_RECUERDA = 'No lo recuerda';

// Lo que se ofrece si el servidor no contesta: solo «No lo recuerda».
export const SIN_CATALOGO = [NO_LO_RECUERDA];

/** El mismo texto que el 400 del servidor. */
export const AVISO_FALTA_ORIGEN = 'Falta “Cómo nos conoció” (si el paciente no lo sabe, elige “No lo recuerda”)';

export const faltaOrigen = (v) => !String(v ?? '').trim();

/**
 * Las opciones del desplegable para un valor dado. Hay miles de fichas con
 * texto libre de antes («Recomendación», «Internet»): ese texto NO se pierde
 * al abrir la ficha. Sale el primero, marcado como valor actual, y se guarda
 * tal cual si no se cambia.
 */
export function opcionesComoNosConocio(valorActual, lista = SIN_CATALOGO) {
  const actual = String(valorActual ?? '').trim();
  const opciones = lista.map((o) => ({ valor: o, etiqueta: o }));
  if (actual && !lista.includes(actual)) {
    return [{ valor: actual, etiqueta: `${actual} (valor actual)` }, ...opciones];
  }
  return opciones;
}

const URL_EQUIPO = '/api/origenes-captacion/nombres';
const URL_PUBLICA = '/api/public/origenes-captacion';
const VIGENCIA_MS = 5 * 60 * 1000;
// Si no contesta, se vuelve a pedir al cabo de estos milisegundos.
export const REINTENTOS_MS = [2000, 5000, 15000];
const guardadas = {};
// Cómo se le enseña cada nombre al paciente (lo manda el servidor con la
// lista pública): { 'Nombre del catálogo': 'Texto entero' }.
let etiquetasDelServidor = {};

/** Los nombres que manda el servidor, limpios y con «No lo recuerda» al
 *  final; null si no sirven (vacío, forma rara). */
export function listaDelServidor(datos) {
  const lista = Array.isArray(datos) ? datos : (Array.isArray(datos?.nombres) ? datos.nombres : null);
  if (!lista) return null;
  const nombres = [...new Set(lista.map((n) => String(n ?? '').trim()).filter(Boolean))]
    .filter((n) => n.toLowerCase() !== NO_LO_RECUERDA.toLowerCase());
  return nombres.length ? [...nombres, NO_LO_RECUERDA] : null;
}

function guardarEtiquetas(datos) {
  const e = datos && !Array.isArray(datos) ? datos.etiquetas : null;
  if (!e || typeof e !== 'object') return;
  etiquetasDelServidor = Object.fromEntries(Object.entries(e)
    .filter(([k, v]) => typeof k === 'string' && typeof v === 'string' && v.trim())
    .map(([k, v]) => [k, v.trim()]));
}

/** Una petición por página y cinco minutos; un fallo no se guarda. Con
 *  `forzar` se vuelve a preguntar (el servidor ha dicho que una opción ya no
 *  existe: dirección cambió el catálogo con la página abierta). La pública
 *  pide también cómo se le enseña cada nombre al paciente. */
export function cargarOpcionesComoNosConocio(publico = false, { forzar = false } = {}) {
  const url = publico ? URL_PUBLICA : URL_EQUIPO;
  const ya = guardadas[url];
  if (!forzar && ya && Date.now() - ya.desde < VIGENCIA_MS) return ya.promesa;
  const peticion = publico ? api.get(url, { params: { etiquetas: 1 } }) : api.get(url);
  const promesa = peticion
    .then((r) => {
      const lista = listaDelServidor(r?.data);
      if (lista) guardarEtiquetas(r?.data);
      return lista;
    })
    .catch(() => null)
    .then((lista) => {
      if (!lista) delete guardadas[url];
      return lista;
    });
  guardadas[url] = { promesa, desde: Date.now() };
  return promesa;
}

/** Para las pruebas: olvidar lo pedido. */
export function olvidarOpcionesComoNosConocio() {
  Object.keys(guardadas).forEach((k) => { delete guardadas[k]; });
  etiquetasDelServidor = {};
}

/**
 * Las opciones del desplegable, las del catálogo vigente en cuanto contesta
 * el servidor (vacío mientras tanto). Si no contesta, «No lo recuerda» y se
 * vuelve a pedir (`REINTENTOS_MS`). Subir `version` las vuelve a pedir sin
 * esperar a que caduquen.
 */
export function useOpcionesComoNosConocio({ publico = false, version = 0 } = {}) {
  const [opciones, setOpciones] = useState([]);
  useEffect(() => {
    let vivo = true;
    let temporizador = null;
    let intento = 0;
    const pedir = (forzar) => {
      cargarOpcionesComoNosConocio(publico, { forzar }).then((lista) => {
        if (!vivo) return;
        if (lista) { setOpciones(lista); return; }
        setOpciones((antes) => (antes.length ? antes : SIN_CATALOGO));
        if (intento < REINTENTOS_MS.length) {
          temporizador = setTimeout(() => pedir(true), REINTENTOS_MS[intento]);
          intento += 1;
        }
      });
    };
    pedir(version > 0);
    return () => { vivo = false; if (temporizador) clearTimeout(temporizador); };
  }, [publico, version]);
  return opciones;
}

/** El 400 del servidor cuando lo elegido ya no está en el catálogo. */
export const esOrigenQueYaNoExiste = (e) => e?.response?.status === 400
  && /ya no est[áa] entre las opciones/i.test(String(e?.response?.data?.detail || ''));

/**
 * En la LOPD del iPad y del enlace de casa elige el propio paciente: las
 * abreviaturas del catálogo se le enseñan enteras. Y la página ENVÍA ese
 * mismo texto (23/9/2026, segunda pasada del supervisor): lo que firma tiene
 * que decir exactamente lo que leyó. La ficha guarda el nombre del catálogo,
 * que lo pone el servidor. La tabla la manda el servidor con la lista pública
 * (`origenes_captacion.ETIQUETAS_PARA_EL_PACIENTE`, V2): aquí no hay copia.
 * Sin ella, el nombre tal cual.
 */
export const etiquetaParaElPaciente = (valor) => etiquetasDelServidor[valor] || valor;
